import React, {lazy, Suspense} from 'react';
import styled, {css} from 'styled-components';
import {transformSizePropToSize} from '../../utils/common';
import {BaseIconContainer} from './styled';

type SvgComponent = React.ComponentType<React.SVGProps<SVGSVGElement>>;

const QuestionCircle = lazy(() => import('../../assets/icons/icon-question-circle.svg?react'));
const WhiteArrowRight = lazy(() => import('../../assets/icons/arrowNext.svg?react'));
const Trash = lazy(() => import('../../assets/icons/rubbish-bin.svg?react'));
const Camera = lazy(() => import('../../assets/icons/camera.svg?react'));
const Picture = lazy(() => import('../../assets/icons/picture-icon.svg?react'));
const Close = lazy(() => import('../../assets/icons/close-icon.svg?react'));
const Padlock = lazy(() => import('../../assets/icons/padlock.svg?react'));
const BackArrow = lazy(() => import('../../assets/icons/back-arrow.svg?react'));
const ArrowRightBlue = lazy(() => import('../../assets/icons/arrow-right-icon-blue.svg?react'));
const Thunder = lazy(() => import('../../assets/icons/tunder_icon.svg?react'));
const EditIcon = lazy(() => import('../../assets/icons/edit-icon.svg?react'));
const QuestionCircleBlue = lazy(() => import('../../assets/icons/question-icon.svg?react'));
const WarningSecondary = lazy(() => import('../../assets/icons/warning-secondary.svg?react'));
const CheckSecondary = lazy(() => import('../../assets/icons/check-secondary.svg?react'));
const CreditCardBlue = lazy(() => import('../../assets/icons/credit-card-blue2.svg?react'));
const CheckOutCalendar = lazy(() => import('../../assets/icons/check-out-calendar.svg?react'));
const CheckInCalendar = lazy(() => import('../../assets/icons/check-in-calendar.svg?react'));
const MultiGuest = lazy(() => import('../../assets/icons/multi-guest.svg?react'));
const BackBlueArrow = lazy(() => import('../../assets/icons/back-blue-arrow.svg?react'));
const Information = lazy(() => import('../../assets/icons/information.svg?react'));
const Plus = lazy(() => import('../../assets/icons/plus-icon.svg?react'));
const LanguageIcon = lazy(() => import('../../assets/icons/language-icon.svg?react'));
const LockIcon = lazy(() => import('../../assets/icons/lock-icon.svg?react'));
const Home = lazy(() => import('../../assets/icons/home.svg?react'));
const KeySidebar = lazy(() => import('../../assets/icons/key-sidebar.svg?react'));
const LanguageSidebar = lazy(() => import('../../assets/icons/language-sidebar.svg?react'));
const LocationSidebar = lazy(() => import('../../assets/icons/location-sidebar.svg?react'));
const LockSidebar = lazy(() => import('../../assets/icons/lock-sidebar.svg?react'));
const CheckFill = lazy(() => import('../../assets/icons/check-fill.svg?react'));
const ShoppingCart = lazy(() => import('../../assets/icons/shopping-cart.svg?react'));
const BlackMenu = lazy(() => import('../../assets/icons/black-menu.svg?react'));
const CloseBlack = lazy(() => import('../../assets/icons/close-black.svg?react'));
const InformationGray = lazy(() => import('../../assets/icons/information-gray.svg?react'));
const ThreeDots = lazy(() => import('../../assets/icons/three-dots.svg?react'));
const RemoteAccessBuilding = lazy(
  () => import('../../assets/icons/remote-access-building.svg?react'),
);
const RemoteAccessApartment = lazy(
  () => import('../../assets/icons/remote-access-apartment.svg?react'),
);
const DoubleArrow = lazy(() => import('../../assets/icons/double-arrow.svg?react'));
const WarningError = lazy(() => import('../../assets/icons/warning-error.svg?react'));
const NotFoundHouse = lazy(() => import('../../assets/img/not-found-house.svg?react'));
const IconRegister = lazy(() => import('../../assets/icons/empty-state.svg?react'));
const ErrorModalIcon = lazy(() => import('../../assets/icons/error.svg?react'));
const Dots = lazy(() => import('../../assets/icons/dots.svg?react'));
const AutoFill = lazy(() => import('../../assets/icons/new-auto-filled-icon.svg?react'));
const ArrowLeft = lazy(() => import('../../assets/icons/arrow-left.svg?react'));
const GuestCircle = lazy(() => import('../../assets/icons/guest-circle.svg?react'));
const GrayRightArrow = lazy(() => import('../../assets/icons/gray-right-arrow.svg?react'));
const KeyCircle = lazy(() => import('../../assets/icons/key-circle.svg?react'));
const ShareArrow = lazy(() => import('../../assets/icons/share-arrow.svg?react'));
const ShoppingCartCircle = lazy(
  () => import('../../assets/icons/shopping-car-circle.svg?react'),
);
const ArrowDates = lazy(() => import('../../assets/icons/arrow-dates.svg?react'));
const SuccessCircle = lazy(() => import('../../assets/icons/success-circle.svg?react'));
const Image = lazy(() => import('../../assets/icons/image.svg?react'));
const BlueTickIcon = lazy(() => import('../../assets/icons/credit-card-blue-tick.svg?react'));
const RedXIcon = lazy(() => import('../../assets/icons/credit-card-red-x.svg?react'));
const CopyLink = lazy(() => import('../../assets/icons/copy-link.svg?react'));
const AddItem = lazy(() => import('../../assets/icons/add-item.svg?react'));
const Wifi = lazy(() => import('../../assets/icons/guidebooks/wifi-icon.svg?react'));
const Accessibility = lazy(
  () => import('../../assets/icons/guidebooks/accessibility-icon.svg?react'),
);
const ArrowLocation = lazy(
  () => import('../../assets/icons/guidebooks/arrow-location.svg?react'),
);
const Bag = lazy(() => import('../../assets/icons/guidebooks/bag.svg?react'));
const GuidebookMainIcon = lazy(
  () => import('../../assets/icons/guidebooks/guidebook-main-icon.svg?react'),
);
const GuidebookIcon = lazy(
  () => import('../../assets/icons/guidebooks/guidebook-icon.svg?react'),
);
const Elevator = lazy(() => import('../../assets/icons/guidebooks/elevator.svg?react'));
const NoSmoking = lazy(() => import('../../assets/icons/guidebooks/no-smoking.svg?react'));
const Gym = lazy(() => import('../../assets/icons/guidebooks/gym.svg?react'));
const Garage = lazy(() => import('../../assets/icons/guidebooks/garage.svg?react'));
const CheckedBadge = lazy(
  () => import('../../assets/icons/guidebooks/checked-badge.svg?react'),
);
const CheckedBadgeTwo = lazy(
  () => import('../../assets/icons/guidebooks/checked-badge-two.svg?react'),
);
const GreetHand = lazy(() => import('../../assets/icons/guidebooks/greet-hand.svg?react'));
const GuidebookLocation = lazy(
  () => import('../../assets/icons/guidebooks/location.svg?react'),
);
const Phone = lazy(() => import('../../assets/icons/guidebooks/phone.svg?react'));
const Plane = lazy(() => import('../../assets/icons/guidebooks/plane.svg?react'));
const EmptyStateCart = lazy(() => import('../../assets/img/empty-state-cart.svg?react'));
const EmptyStateList = lazy(() => import('../../assets/img/empty-state-list.svg?react'));
const Tag = lazy(() => import('../../assets/icons/tag.svg?react'));
const MagnifierGray = lazy(() => import('../../assets/icons/magnifier-gray.svg?react'));
const Arrow = lazy(() => import('../../assets/icons/arrow-down.svg?react'));
const UnavailableBooks = lazy(() => import('../../assets/icons/unavailable-books.svg?react'));
const Download = lazy(() => import('../../assets/icons/download-icon.svg?react'));
const ShareDownload = lazy(() => import('../../assets/icons/share-download.svg?react'));
const DeleteItemBox = lazy(() => import('../../assets/icons/delete_item_box.svg?react'));
const Calendar = lazy(() => import('../../assets/icons/calendar.svg?react'));
const ExperiencesUnits = lazy(() => import('../../assets/icons/experiences-units.svg?react'));
const TicketCircle = lazy(() => import('../../assets/icons/ticket-circle.svg?react'));
const Minus = lazy(() => import('../../assets/icons/minus.svg?react'));
const DotList = lazy(() => import('../../assets/icons/guidebooks/dot-list.svg?react'));
const OpenBook = lazy(() => import('../../assets/icons/guidebooks/open-book.svg?react'));
const Map = lazy(() => import('../../assets/icons/guidebooks/map.svg?react'));
const CheckList = lazy(() => import('../../assets/icons/guidebooks/check-list.svg?react'));
const Layout = lazy(() => import('../../assets/icons/guidebooks/layout.svg?react'));
const AboutPeople = lazy(() => import('../../assets/icons/guidebooks/about-people.svg?react'));
const Weather = lazy(() => import('../../assets/icons/guidebooks/weather.svg?react'));
const InfoFilled = lazy(() => import('../../assets/icons/info-filled.svg?react'));
const Sheet = lazy(() => import('../../assets/icons/guidebooks/sheet.svg?react'));
const Attractions = lazy(() => import('../../assets/icons/guidebooks/attractions.svg?react'));
const Apartments = lazy(() => import('../../assets/icons/guidebooks/apartments.svg?react'));
const Restaurant = lazy(() => import('../../assets/icons/guidebooks/restaurant.svg?react'));
const AcDual = lazy(() => import('../../assets/icons/guidebooks/ac-dual.svg?react'));
const AirConditioner = lazy(
  () => import('../../assets/icons/guidebooks/air-conditioner.svg?react'),
);
const BabyChangingRoom = lazy(
  () => import('../../assets/icons/guidebooks/baby-changing-room.svg?react'),
);
const BagTwo = lazy(() => import('../../assets/icons/guidebooks/bag-2.svg?react'));
const Bed = lazy(() => import('../../assets/icons/guidebooks/bed.svg?react'));
const BoardGames = lazy(() => import('../../assets/icons/guidebooks/board-games.svg?react'));
const Bug = lazy(() => import('../../assets/icons/guidebooks/bug.svg?react'));
const ChairWheels = lazy(() => import('../../assets/icons/guidebooks/chair-wheels.svg?react'));
const Cleaning = lazy(() => import('../../assets/icons/guidebooks/cleaning.svg?react'));
const ClosetHanger = lazy(
  () => import('../../assets/icons/guidebooks/closet-hanger.svg?react'),
);
const CoffeeMaker = lazy(() => import('../../assets/icons/guidebooks/coffee-maker.svg?react'));
const Coffee = lazy(() => import('../../assets/icons/guidebooks/coffee.svg?react'));
const ColdMode = lazy(() => import('../../assets/icons/guidebooks/cold-mode.svg?react'));
const Crib = lazy(() => import('../../assets/icons/guidebooks/crib.svg?react'));
const Fan = lazy(() => import('../../assets/icons/guidebooks/fan.svg?react'));
const Fireplace = lazy(() => import('../../assets/icons/guidebooks/fireplace.svg?react'));
const Fridge = lazy(() => import('../../assets/icons/guidebooks/fridge.svg?react'));
const HairDryer = lazy(() => import('../../assets/icons/guidebooks/hair-dryer.svg?react'));
const Health = lazy(() => import('../../assets/icons/guidebooks/health.svg?react'));
const Heat = lazy(() => import('../../assets/icons/guidebooks/heat.svg?react'));
const HomeStorage = lazy(() => import('../../assets/icons/guidebooks/home-storage.svg?react'));
const Iron = lazy(() => import('../../assets/icons/guidebooks/iron.svg?react'));
const Kettle = lazy(() => import('../../assets/icons/guidebooks/kettle.svg?react'));
const KingBed = lazy(() => import('../../assets/icons/guidebooks/king-bed.svg?react'));
const LaundryMachine = lazy(
  () => import('../../assets/icons/guidebooks/laundry-machine.svg?react'),
);
const LuggageTwo = lazy(() => import('../../assets/icons/guidebooks/luggage-2.svg?react'));
const Luggage = lazy(() => import('../../assets/icons/guidebooks/luggage.svg?react'));
const Metro = lazy(() => import('../../assets/icons/guidebooks/metro.svg?react'));
const Microwave = lazy(() => import('../../assets/icons/guidebooks/microwave.svg?react'));
const MicrowaveTwo = lazy(() => import('../../assets/icons/guidebooks/microwave-2.svg?react'));
const OutdoorGrill = lazy(
  () => import('../../assets/icons/guidebooks/outdoor-grill.svg?react'),
);
const Oven = lazy(() => import('../../assets/icons/guidebooks/oven.svg?react'));
const PetFriendly = lazy(() => import('../../assets/icons/guidebooks/pet-friendly.svg?react'));
const Pool = lazy(() => import('../../assets/icons/guidebooks/pool.svg?react'));
const RoomService = lazy(() => import('../../assets/icons/guidebooks/room-service.svg?react'));
const SecurityCamera = lazy(
  () => import('../../assets/icons/guidebooks/security-camera.svg?react'),
);
const SecurityPerson = lazy(
  () => import('../../assets/icons/guidebooks/security-person.svg?react'),
);
const Security = lazy(() => import('../../assets/icons/guidebooks/security.svg?react'));
const Shopping = lazy(() => import('../../assets/icons/guidebooks/shopping.svg?react'));
const Shower = lazy(() => import('../../assets/icons/guidebooks/shower.svg?react'));
const Sofa = lazy(() => import('../../assets/icons/guidebooks/sofa.svg?react'));
const Spa = lazy(() => import('../../assets/icons/guidebooks/spa.svg?react'));
const Stockpot = lazy(() => import('../../assets/icons/guidebooks/stockpot.svg?react'));
const Train = lazy(() => import('../../assets/icons/guidebooks/train.svg?react'));
const Tv = lazy(() => import('../../assets/icons/guidebooks/tv.svg?react'));
const Vacuum = lazy(() => import('../../assets/icons/guidebooks/vacuum.svg?react'));
const Google = lazy(() => import('../../assets/icons/google.svg?react'));
const Apple = lazy(() => import('../../assets/icons/apple.svg?react'));
const NoLogin = lazy(() => import('../../assets/icons/no-login.svg?react'));
const GuestRegistration = lazy(() => import('../../assets/icons/guest-registration.svg?react'));
const Logout = lazy(() => import('../../assets/icons/logout.svg?react'));
const CarCircle = lazy(() => import('../../assets/icons/car-circle.svg?react'));
const AddGuestTwo = lazy(() => import('../../assets/icons/add-guest-2.svg?react'));
const PlaneDetach = lazy(() => import('../../assets/icons/plane-detach.svg?react'));

export type IconNames =
  | 'whiteArrowRight'
  | 'trash'
  | 'camera'
  | 'picture'
  | 'close'
  | 'padlock'
  | 'backArrow'
  | 'arrowRightBlue'
  | 'editIcon'
  | 'thunder'
  | 'questionCircleBlue'
  | 'warningSecondary'
  | 'checkSecondary'
  | 'creditCardBlue'
  | 'checkOutCalendar'
  | 'checkInCalendar'
  | 'multiGuest'
  | 'backBlueArrow'
  | 'plus'
  | 'information'
  | 'languageIcon'
  | 'lockIcon'
  | 'keySidebar'
  | 'languageSidebar'
  | 'locationSidebar'
  | 'lockSidebar'
  | 'checkFill'
  | 'shoppingCart'
  | 'blackMenu'
  | 'closeBlack'
  | 'informationGray'
  | 'threeDots'
  | 'notFoundHouse'
  | 'iconRegister'
  | 'errorModalIcon'
  | 'dots'
  | 'autoFill'
  | 'arrowLeft'
  | 'remoteAccessBuilding'
  | 'remoteAccessApartment'
  | 'doubleArrow'
  | 'warningError'
  | 'guestCircle'
  | 'grayRightArrow'
  | 'keyCircle'
  | 'shareArrow'
  | 'shoppingCartCircle'
  | 'arrowDates'
  | 'successCircle'
  | 'image'
  | 'blueTickIcon'
  | 'redXIcon'
  | 'copyLink'
  | 'addItem'
  | 'wifi'
  | 'accessibility'
  | 'elevator'
  | 'no-smoking'
  | 'gym'
  | 'garage'
  | 'checkedBadge'
  | 'checkedBadgeTwo'
  | 'greetHand'
  | 'home'
  | 'location'
  | 'phone'
  | 'plane'
  | 'questionCircle'
  | 'arrowLocation'
  | 'bag'
  | 'guideBookMainIcon'
  | 'guidebookIcon'
  | 'emptyStateCart'
  | 'emptyStateList'
  | 'tag'
  | 'magnifierGray'
  | 'arrow'
  | 'unavailable-books'
  | 'download'
  | 'shareDownload'
  | 'deleteItemBox'
  | 'calendar'
  | 'experiencesUnits'
  | 'ticketCircle'
  | 'minus'
  | 'dot-list'
  | 'open-book'
  | 'map'
  | 'check-list'
  | 'layout'
  | 'about-people'
  | 'weather'
  | 'info-filled'
  | 'sheet'
  | 'attractions'
  | 'apartments'
  | 'restaurant'
  | 'ac-dual'
  | 'air-conditioner'
  | 'baby-changing-room'
  | 'bed'
  | 'board-games'
  | 'bug'
  | 'chair-wheels'
  | 'cleaning'
  | 'closet-hanger'
  | 'coffee-maker'
  | 'coffee'
  | 'cold-mode'
  | 'crib'
  | 'fan'
  | 'fireplace'
  | 'fridge'
  | 'hair-dryer'
  | 'health'
  | 'heat'
  | 'home-storage'
  | 'iron'
  | 'kettle'
  | 'king-bed'
  | 'laundry-machine'
  | 'luggage'
  | 'metro'
  | 'microwave'
  | 'outdoor-grill'
  | 'oven'
  | 'pet-friendly'
  | 'pool'
  | 'room-service'
  | 'security-camera'
  | 'security-person'
  | 'security'
  | 'shopping'
  | 'shower'
  | 'sofa'
  | 'spa'
  | 'stockpot'
  | 'train'
  | 'tv'
  | 'vacuum'
  | 'microwave-two'
  | 'luggage-two'
  | 'bag-two'
  | 'google'
  | 'apple'
  | 'no-login'
  | 'guest-registration'
  | 'logout'
  | 'carCircle'
  | 'addGuestTwo'
  | 'plane-detach';

export type IconsHash = {
  [K in IconNames]: React.LazyExoticComponent<SvgComponent>;
};

const icons: IconsHash = {
  questionCircle: QuestionCircle,
  whiteArrowRight: WhiteArrowRight,
  trash: Trash,
  camera: Camera,
  picture: Picture,
  close: Close,
  padlock: Padlock,
  backArrow: BackArrow,
  arrowRightBlue: ArrowRightBlue,
  editIcon: EditIcon,
  thunder: Thunder,
  questionCircleBlue: QuestionCircleBlue,
  warningSecondary: WarningSecondary,
  checkSecondary: CheckSecondary,
  creditCardBlue: CreditCardBlue,
  checkOutCalendar: CheckOutCalendar,
  checkInCalendar: CheckInCalendar,
  multiGuest: MultiGuest,
  backBlueArrow: BackBlueArrow,
  information: Information,
  plus: Plus,
  languageIcon: LanguageIcon,
  lockIcon: LockIcon,
  home: Home,
  keySidebar: KeySidebar,
  languageSidebar: LanguageSidebar,
  locationSidebar: LocationSidebar,
  lockSidebar: LockSidebar,
  checkFill: CheckFill,
  shoppingCart: ShoppingCart,
  blackMenu: BlackMenu,
  closeBlack: CloseBlack,
  informationGray: InformationGray,
  threeDots: ThreeDots,
  remoteAccessBuilding: RemoteAccessBuilding,
  remoteAccessApartment: RemoteAccessApartment,
  doubleArrow: DoubleArrow,
  warningError: WarningError,
  notFoundHouse: NotFoundHouse,
  iconRegister: IconRegister,
  errorModalIcon: ErrorModalIcon,
  dots: Dots,
  logout: Logout,
  autoFill: AutoFill,
  arrowLeft: ArrowLeft,
  guestCircle: GuestCircle,
  grayRightArrow: GrayRightArrow,
  keyCircle: KeyCircle,
  shareArrow: ShareArrow,
  shoppingCartCircle: ShoppingCartCircle,
  arrowDates: ArrowDates,
  successCircle: SuccessCircle,
  image: Image,
  blueTickIcon: BlueTickIcon,
  redXIcon: RedXIcon,
  copyLink: CopyLink,
  addItem: AddItem,
  wifi: Wifi,
  accessibility: Accessibility,
  elevator: Elevator,
  'no-smoking': NoSmoking,
  'guest-registration': GuestRegistration,
  gym: Gym,
  garage: Garage,
  checkedBadge: CheckedBadge,
  checkedBadgeTwo: CheckedBadgeTwo,
  greetHand: GreetHand,
  location: GuidebookLocation,
  phone: Phone,
  plane: Plane,
  arrowLocation: ArrowLocation,
  bag: Bag,
  guideBookMainIcon: GuidebookMainIcon,
  guidebookIcon: GuidebookIcon,
  emptyStateCart: EmptyStateCart,
  emptyStateList: EmptyStateList,
  tag: Tag,
  magnifierGray: MagnifierGray,
  arrow: Arrow,
  'unavailable-books': UnavailableBooks,
  download: Download,
  shareDownload: ShareDownload,
  deleteItemBox: DeleteItemBox,
  calendar: Calendar,
  experiencesUnits: ExperiencesUnits,
  ticketCircle: TicketCircle,
  minus: Minus,
  'dot-list': DotList,
  'open-book': OpenBook,
  map: Map,
  'check-list': CheckList,
  layout: Layout,
  'about-people': AboutPeople,
  weather: Weather,
  'info-filled': InfoFilled,
  sheet: Sheet,
  attractions: Attractions,
  apartments: Apartments,
  restaurant: Restaurant,
  'ac-dual': AcDual,
  'air-conditioner': AirConditioner,
  'baby-changing-room': BabyChangingRoom,
  bed: Bed,
  'board-games': BoardGames,
  bug: Bug,
  'chair-wheels': ChairWheels,
  cleaning: Cleaning,
  'closet-hanger': ClosetHanger,
  'coffee-maker': CoffeeMaker,
  coffee: Coffee,
  'cold-mode': ColdMode,
  crib: Crib,
  fan: Fan,
  fireplace: Fireplace,
  fridge: Fridge,
  'hair-dryer': HairDryer,
  health: Health,
  heat: Heat,
  'home-storage': HomeStorage,
  iron: Iron,
  kettle: Kettle,
  'king-bed': KingBed,
  'laundry-machine': LaundryMachine,
  'luggage-two': LuggageTwo,
  luggage: Luggage,
  metro: Metro,
  microwave: Microwave,
  'microwave-two': MicrowaveTwo,
  'outdoor-grill': OutdoorGrill,
  oven: Oven,
  'pet-friendly': PetFriendly,
  pool: Pool,
  'room-service': RoomService,
  'security-camera': SecurityCamera,
  'security-person': SecurityPerson,
  security: Security,
  shopping: Shopping,
  shower: Shower,
  sofa: Sofa,
  spa: Spa,
  stockpot: Stockpot,
  train: Train,
  tv: Tv,
  vacuum: Vacuum,
  'bag-two': BagTwo,
  google: Google,
  apple: Apple,
  'no-login': NoLogin,
  carCircle: CarCircle,
  addGuestTwo: AddGuestTwo,
  'plane-detach': PlaneDetach,
};

export type IconProps = {
  name: IconNames;
  title?: string;
  className?: string;
  containerClassName?: string;
  size?: string | number;
  color?: string;
  custom?: boolean;
  $transparentSvg?: boolean;
  'aria-hidden'?: string;
} & React.HTMLAttributes<HTMLSpanElement>;

const StyledSVGIcon = styled.span<{
  color?: string;
  size?: string | number;
  $custom?: string;
  $transparentSvg?: boolean;
}>`
  display: inline-flex;
  width: ${props => transformSizePropToSize(props.size) || '24px'};
  height: ${props => transformSizePropToSize(props.size) || '24px'};
  fill: ${props => (props.$transparentSvg ? 'transparent' : props.color || 'none')};
  color: ${props => props.color || 'none'};

  ${props => {
    if (props.color && props.color !== 'transparent') {
      return css`
        &:not(.chekin-empty-state__icon):not(.guest-register-view__icon-register):not(
            .error-view__icon
          ):not(.autofill-modal-sheet__icon) {
          path {
            fill: ${props.color};
          }
        }
      `;
    }
  }}

  ${props => {
    return props.$custom
      ? css`
          --icon-color: ${props.color || 'var(--brand-icons)'};

          .system-path {
            fill: ${props.color || 'var(--brand-icons)'};
          }
          #path-group {
            path,
            rect {
              fill: ${props.color || 'var(--brand-icons)'};
            }
          }
          // styles for No register Guest svg AND 3 dots menu icon svg
          #empty-state-register-guest-linear-1,
          #empty-state-register-guest-linear-2,
          #sigle-dot-icon-linear-1,
          #sigle-dot-icon-linear-2,
          #sigle-dot-icon-linear-3,
          .auto-fill-icon-linear,
          .linear-svg-group {
            .stop-one {
              stop-color: ${props.color || 'var(--brand-icons)'};
            }

            .stop-two {
              stop-color: ${props.color || 'var(--brand-icons)'};
              opacity: 0.3;
            }
          }
          // end styles for No register Guest svg
          // styles for Error modal svg
          .primary-svg-color {
            fill: ${props.color || 'var(--brand-icons)'};
            stroke: ${props.color || 'var(--brand-icons)'};
            opacity: 0.3;
          }
          .secondary-svg-color {
            fill: ${props.color || 'var(--brand-icons)'};
            stroke: ${props.color || 'var(--brand-icons)'};
          }
          //end styles for Error modal svg

          .vector_1 {
            stroke: #dcdcdc;
            fill: none;
          }
          .vector_2 {
            stroke: #dcdcdc;
            fill: none;
          }

          .circle-background {
            fill: var(--background-secondary);
          }
        `
      : css`
          .system-path,
          path {
            fill: ${props.color || 'var(--brand-icons)'};
          }
        `;
  }}
`;

/** @deprecated Use Icon(ui-kit) component instead **/
const CustomIcon = (props: IconProps) => {
  const IconComponent = icons[props.name];
  const resolvedSize = transformSizePropToSize(props.size) || '24px';
  return (
    <BaseIconContainer
      size={props.size}
      className={`custom-icon ${props.containerClassName}`}
    >
      <Suspense
        fallback={
          <span
            style={{display: 'inline-flex', width: resolvedSize, height: resolvedSize}}
          />
        }
      >
        <StyledSVGIcon
          size={props.size}
          color={props.color || 'transparent'}
          className={'custom-icon__icon ' + props.className}
          $custom={props.custom ? 'true' : 'false'}
          $transparentSvg={props.$transparentSvg}
          aria-hidden={props['aria-hidden']}
          aria-label={props.title || props.name}
        >
          <IconComponent width="100%" height="100%" />
        </StyledSVGIcon>
      </Suspense>
    </BaseIconContainer>
  );
};

export {CustomIcon};
